import { Form } from 'formik'
import IconConfirm from '/public/icons/icon-book-confirm.svg'
import Radio from '../../../../../../../../../elements/form/Radio'
import { useEffect, useState } from 'react'
import capitalize from 'lodash-es/capitalize'
import Button from '../../../../../../../../../elements/Button'
import CustomField from '../../../../../../../../../elements/form/CustomField'
import styles from './styles.module.scss'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { useSearchContext } from '/providers/SearchContext'
import moment from 'moment'
import {
  getUserContact,
  saveUserContact
} from '/components/helpers/userContactStorage'

const ThankYou = ({
  setFieldValue,
  translations,
  reservationData,
  isSubmitting
}) => {
  const router = useRouter()
  const [transport, setTransport] = useState('')
  const [details, setDetails] = useState('')
  const [pets, setPets] = useState('')
  const [arrangements, setArrangements] = useState('')
  const [grocery, setGrocery] = useState('')
  const [phone, setPhone] = useState('')
  const { setReset, setReserved } = useSearchContext()

  // Load saved phone number on mount
  useEffect(() => {
    const contact = getUserContact()
    if (contact.phone) {
      setPhone(contact.phone)
      setFieldValue('phone', contact.phone)
    }
  }, [setFieldValue])

  // Reset search state on mount - intentionally runs only once to trigger
  // search context reset when the ThankYou component mounts after booking
  useEffect(() => {
    setReset(true)
    setReserved(true)
    setTimeout(() => {
      setReset(false)
      setReserved(false)
    }, 100)
    // Dependencies intentionally empty - run only once on mount
    // eslint-disable-next-line
  }, [])

  // Handler to save phone on blur
  const handlePhoneBlur = (value) => {
    if (value) {
      saveUserContact({ phone: value })
    }
  }

  // Format dates for display with locale support
  const formatDate = (dateString) => {
    return moment(dateString).locale(router.locale).format('MMMM D, YYYY')
  }

  // Format currency for display
  const formatCurrency = (cents) => {
    const dollars = cents / 100
    return `$${dollars.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`
  }

  const checkboxesUnknown = [
    translations?.yes[router.locale],
    translations?.no[router.locale],
    translations?.unknown[router.locale]
  ]

  const checkboxes = [
    translations?.yes[router.locale],
    translations?.no[router.locale]
  ]

  return (
    <Form className={classNames(styles.form, styles.thanks)}>
      <div className={styles.form__intro}>
        <IconConfirm />

        <h2 className={styles.form__title}>
          {translations?.title[router.locale]}
        </h2>

        <p>{translations?.subtitle[router.locale]}</p>
      </div>

      {/* Booking Confirmation Details */}
      {reservationData && (
        <div className={styles.confirmation_details}>
          <h3 className={styles.confirmation_details__title}>
            {translations?.bookingConfirmation?.[router.locale] ||
              'Booking Confirmation'}
          </h3>

          <div className={styles.confirmation_details__grid}>
            <div
              className={classNames(
                styles.confirmation_details__item,
                styles['confirmation_details__item--full']
              )}
            >
              <span className={styles.confirmation_details__label}>
                {translations?.propertyName?.[router.locale] || 'Property'}:
              </span>
              <span className={styles.confirmation_details__value}>
                {reservationData.property_title}
              </span>
            </div>

            <div
              className={classNames(
                styles.confirmation_details__item,
                styles['confirmation_details__item--full']
              )}
            >
              <span className={styles.confirmation_details__label}>
                {translations?.depositReserved?.[router.locale] ||
                  'Deposit Reserved'}
                :
              </span>
              <span className={styles.confirmation_details__value}>
                {formatCurrency(reservationData.amounts.deposit_cents)}
                <span className={styles.confirmation_details__note_inline}>
                  {' '}
                  {translations?.depositNote?.[router.locale] ||
                    '(will be charged upon confirmation)'}
                </span>
              </span>
            </div>

            <div className={styles.confirmation_details__item}>
              <span className={styles.confirmation_details__label}>
                {translations?.checkIn?.[router.locale] || 'Check-in'}:
              </span>
              <span className={styles.confirmation_details__value}>
                {formatDate(reservationData.start_date)}
              </span>
            </div>

            <div className={styles.confirmation_details__item}>
              <span className={styles.confirmation_details__label}>
                {translations?.checkOut?.[router.locale] || 'Check-out'}:
              </span>
              <span className={styles.confirmation_details__value}>
                {formatDate(reservationData.end_date)}
              </span>
            </div>

            <div
              className={classNames(
                styles.confirmation_details__item,
                styles['confirmation_details__item--full']
              )}
            >
              <span className={styles.confirmation_details__label}>
                {translations?.confirmationNumber?.[router.locale] ||
                  'Confirmation Number'}
                :
              </span>
              <span
                className={classNames(
                  styles.confirmation_details__value,
                  styles['confirmation_details__value--highlight']
                )}
              >
                {reservationData.confirmation_code}
              </span>
            </div>
          </div>

          <p className={styles.confirmation_details__note}>
            {translations?.emailSent?.[router.locale] ||
              'A confirmation email has been sent to your email address.'}
          </p>
        </div>
      )}

      <label className={styles.form__label}>
        {translations?.phoneNumber[router.locale]}
      </label>

      <CustomField
        name="phone"
        optional
        onChange={(e) => {
          const cleanedValue = e.target.value.replace(/[^0-9+()]/g, '')
          setFieldValue('phone', cleanedValue)
          setPhone(cleanedValue)
        }}
        onBlur={(e) => handlePhoneBlur(e.target.value)}
        value={phone}
        type="tel"
        variant={['full-width', 'white']}
      />

      <label className={styles.form__label}>
        {translations?.rentCar[router.locale]}
      </label>

      {checkboxes.map((item, index) => (
        <Radio
          variant={['white']}
          key={`car-${index}`}
          name="car"
          checkbox
          value={item}
          label={capitalize(item)}
          checked={transport === item}
          onChange={(e) => {
            setTransport(e.target.value)
            setFieldValue('car', e.target.value)
          }}
        />
      ))}

      <p className={styles.form__additional}>
        {translations?.rentCarNote[router.locale]}
      </p>

      <label className={styles.form__label}>
        {translations?.transport[router.locale]}
      </label>

      {checkboxes.map((item, index) => (
        <Radio
          variant={['white']}
          key={`transport-${index}`}
          name="transport"
          checkbox
          value={item}
          label={capitalize(item)}
          checked={pets === item}
          onChange={(e) => {
            setPets(e.target.value)
            setFieldValue('transport', e.target.value)
          }}
        />
      ))}
      <p className={styles.form__additional}>
        {translations?.transportNote[router.locale]}
      </p>

      <label className={styles.form__label}>
        {translations?.assistance[router.locale]}
      </label>

      {checkboxesUnknown.map((item, index) => (
        <Radio
          variant={['white']}
          key={`assistance-${index}`}
          name="assistance"
          checkbox
          value={item}
          label={capitalize(item)}
          checked={arrangements === item}
          onChange={(e) => {
            setArrangements(e.target.value)
            setFieldValue('assistance', e.target.value)
          }}
        />
      ))}
      <p className={styles.form__additional}>
        {translations?.assistanceNote[router.locale]}
      </p>

      <label className={styles.form__label}>
        {translations?.food[router.locale]}
      </label>

      {checkboxesUnknown.map((item, index) => (
        <Radio
          variant={['white']}
          key={`food-${index}`}
          name="food"
          checkbox
          value={item}
          label={capitalize(item)}
          checked={grocery === item}
          onChange={(e) => {
            setGrocery(e.target.value)
            setFieldValue('food', e.target.value)
          }}
        />
      ))}

      <label className={styles.form__label}>
        {translations?.foodNote[router.locale]}
      </label>

      {checkboxesUnknown.map((item, index) => (
        <Radio
          variant={['white']}
          key={`reservation-${index}`}
          name="reservation"
          checkbox
          value={item}
          label={capitalize(item)}
          checked={details === item}
          onChange={(e) => {
            setDetails(e.target.value)
            setFieldValue('reservation', e.target.value)
          }}
        />
      ))}

      <Button
        type="submit"
        arrow={false}
        className={styles.form__submit}
        variant={['color-accent']}
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Submitting...' : translations?.submit[router.locale]}
      </Button>
    </Form>
  )
}

export default ThankYou
